import { useEffect, useState } from 'react';

type UseCountUpOptions = {
	durationMs?: number;
	enabled?: boolean;
};

function usePrefersReducedMotion() {
	const [reducedMotion, setReducedMotion] = useState(false);

	useEffect(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
			return;
		}

		const media = window.matchMedia('(prefers-reduced-motion: reduce)');
		const update = () => setReducedMotion(media.matches);
		update();

		if (typeof media.addEventListener === 'function') {
			media.addEventListener('change', update);
			return () => media.removeEventListener('change', update);
		}

		media.addListener(update);
		return () => media.removeListener(update);
	}, []);

	return reducedMotion;
}

export function useCountUp(target: number, options: UseCountUpOptions = {}) {
	const { durationMs = 1300, enabled = true } = options;
	const prefersReducedMotion = usePrefersReducedMotion();
	const [value, setValue] = useState(() => (enabled ? 0 : target));

	useEffect(() => {
		if (!enabled) {
			setValue(target);
			return;
		}

		if (prefersReducedMotion) {
			setValue(target);
			return;
		}

		const start = performance.now();
		let rafId = 0;

		const tick = (now: number) => {
			const elapsed = now - start;
			const progress = Math.min(elapsed / durationMs, 1);
			const eased = 1 - Math.pow(1 - progress, 3);
			setValue(target * eased);

			if (progress < 1) {
				rafId = window.requestAnimationFrame(tick);
			}
		};

		rafId = window.requestAnimationFrame(tick);
		return () => window.cancelAnimationFrame(rafId);
	}, [durationMs, enabled, prefersReducedMotion, target]);

	return value;
}
